import {AppThunkAction} from 'src/~store/models/AppThunkAction';

import {IOperator} from '../models/Operator';

import { 
    GetAllOperators, 
    SaveOperator,
    DeleteOperator,
    CreateOperatorAction,
    CancelCreateOperatorAction,
    ChangeOperatorAvatar,
    SaveOperatorSettingAction,
    ToggleOperatorSortingAction,
    DownloadBotScript,
} from './interfaces';

import { 
    GET_ALL_OPERATORS_START, 
    GET_ALL_OPERATORS_SUCCESS, 
    GET_ALL_OPERATORS_ERROR, 
    SAVE_OPERATOR_START,
    SAVE_OPERATOR_ERROR, 
    DELETE_OPERATOR_START,
    DELETE_OPERATOR_SUCCESS,
    DELETE_OPERATOR_ERROR,
    CREATE_OPERATOR,
    CANCEL_CREATE_OPERATOR,
    DELETE_NEW_OPERATOR_SUCCESS,
    SAVE_OPERATOR_SETTING,
    TOGGLE_OPERATOR_SORTING as TOGGLE_OPERATOR_SORTING,
    DOWNLOAD_BOT_SCRIPT_START,
    DOWNLOAD_BOT_SCRIPT_SUCCESS,
    DOWNLOAD_BOT_SCRIPT_ERROR
} from './types';

import { 
    getAllOperatorsAsync,
    saveOperatorAsync,
    deleteOperatorAsync,
    changeOperatorAvatarAsync,
    saveOperatorSettingAsync,
    deleteOperatorSettingAsync,
    getSettingsByOperatorsIdAsync,
    downloadBotScriptAsync
} from '../../~api/actions';
import {showSuccessToastr, showErrorToastr} from 'src/app/shared/helpers/toastr-helper/ToastrHelper';

import { OperatorType } from '../models/enums/OperatorType';
import { IApiActionResult } from 'src/~api/ApiActionResult';
import { initNewSetting } from 'src/app/setting/~store/state/InitSettingsState';
import { OPERATOR_SETTING_PREFIX } from '../../operators-main/operators-list/operator-item/OperatorItemConstants';
import i18n from "src/app/shared/localization/i18n";
import { getCustomerId } from 'src/app/shared/helpers/storage-helper/StoragHelper';
import { onOperatorSendFileAsync } from 'src/app/chats-panel/~api/actions';
import { MAX_INT32 } from 'src/app/shared/helpers/file-helper/FileHelper';

// operators list

export const getAllOperators = (): AppThunkAction<GetAllOperators> => async dispatch => {
    dispatch({type: GET_ALL_OPERATORS_START});

    const result = await getAllOperatorsAsync();

    if(result.isSuccess) {
        const operators = result.value ? result.value : [];
        
        const settingResult = await getSettingsByOperatorsIdAsync(operators.map(o => o.operatorId));

        if(settingResult.isSuccess) {
            operators.forEach(operator => operator.operatorSetting = settingResult.value?.find(s => s.id === OPERATOR_SETTING_PREFIX + operator.operatorId) || initNewSetting());
            dispatch( {type: GET_ALL_OPERATORS_SUCCESS, payload: {operators}} );
            return;
        }
    }

    dispatch({type: GET_ALL_OPERATORS_ERROR});
};

export const saveOperator = (oldOperator: IOperator, newOperator: IOperator, scriptFile?: File): AppThunkAction<SaveOperator> => async (dispatch, getState) => {
    dispatch({type: SAVE_OPERATOR_START});

    if(
        (oldOperator.login === newOperator.login) &&
        (oldOperator.displayName === newOperator.displayName) &&
        (oldOperator.name === newOperator.name) &&
        (oldOperator.password === newOperator.password) &&
        (oldOperator.type === newOperator.type) &&
        (oldOperator.description === newOperator.description) &&
        (oldOperator.enabled === newOperator.enabled) &&
        (oldOperator.operatorId !== undefined) &&
        (scriptFile === undefined)) {
            showSuccessToastr(i18n.t('operators.saveOperatorNoChangesMessage'));
            dispatch({type: SAVE_OPERATOR_ERROR})
            return;
    }

    const result = await saveOperatorAsync(newOperator, getState().chatItemsState.operatorId, scriptFile);
    if (!result.isSuccess) {
        result.tryShowErrorToastr();
    }
    else {
        showSuccessToastr(i18n.t('operators.saveOperatorSuccessMessage'));
    }

    getAllOperators()(dispatch, getState);
}

export const deleteOperator = (operator: IOperator): AppThunkAction<DeleteOperator> => async (dispatch, getState) => {
    dispatch({type: DELETE_OPERATOR_START});
    if(operator.operatorId === undefined) {
        dispatch({type: DELETE_NEW_OPERATOR_SUCCESS});
        dispatch({type: DELETE_OPERATOR_SUCCESS});
    } else {
        await deleteOperatorSettingAsync(operator.operatorId);
        const result = await deleteOperatorAsync(operator);

        if (result.isSuccess) {
            dispatch({type: DELETE_OPERATOR_SUCCESS});
            showSuccessToastr(i18n.t('operators.deleteOperatorSuccessMessage'));
        } else {
            dispatch({ type: DELETE_OPERATOR_ERROR });
            const error = JSON?.parse(result.errorText ?? "null");
            if (error.errorCode === "operators.cannotDeleteYourself")
                showErrorToastr(i18n.t("operators.cannotDeleteYourself"));
            else
                showErrorToastr(i18n.t('operators.deleteOperatorErrorMessage'));
        }
    }

    getAllOperators()(dispatch, getState);
}

export const downloadBotScript = (operator: IOperator, fileName: string): AppThunkAction<DownloadBotScript> => async (dispatch, getState) => {
    dispatch({ type: DOWNLOAD_BOT_SCRIPT_START });
    const result = await downloadBotScriptAsync(operator, fileName);

    if (result.isSuccess) {
        dispatch({ type: DOWNLOAD_BOT_SCRIPT_SUCCESS });
    } else {
        showErrorToastr(i18n.t('operators.downloadBotScriptError'));
        dispatch({ type: DOWNLOAD_BOT_SCRIPT_ERROR });
    }
}

export const createOperator = (): AppThunkAction<CreateOperatorAction> => async dispatch => {
    dispatch({type: CREATE_OPERATOR}); 
}

export const cancelCreateOperator = (): AppThunkAction<CancelCreateOperatorAction> => async dispatch => {
    dispatch({type: CANCEL_CREATE_OPERATOR}); 
}

export const changeOperatorAvatar = (file: File, operator: IOperator, onImageChangeSuccess: (imageId: string | null) => void
): AppThunkAction<ChangeOperatorAvatar> => async (dispatch, getState) => {

    const fileResult = await onOperatorSendFileAsync(file, getCustomerId(), MAX_INT32);

    if(fileResult.isSuccess && fileResult.value) {
        const result = await changeOperatorAvatarAsync(operator.operatorId, fileResult.value);
        if(result.isSuccess) {
            showSuccessToastr(i18n.t('operators.setAvatarSuccess'));
            onImageChangeSuccess?.(fileResult.value);
        }
    }
}

export const saveOperatorSetting = (
    operator: IOperator, 
    setting: string
): AppThunkAction<SaveOperatorSettingAction> => async (dispatch) => {
    const result = await saveOperatorSettingAsync(operator.operatorId as number, setting);

    if (result.isSuccess) {
        showSuccessToastr(i18n.t('settings.settingSaved'));
        dispatch({type: SAVE_OPERATOR_SETTING, 
            payload: {
                setting, 
                operator
            }
        });
    }
}

export const toggleOperatorSorting = (): AppThunkAction<ToggleOperatorSortingAction> => async dispatch => {
    dispatch({type: TOGGLE_OPERATOR_SORTING})
}
